import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Luggage, FileText, MapPin, CalendarDays, ArrowRight, Plane } from "lucide-react";
import api, { fcfa } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BOOKING_BADGE = {
  en_attente_paiement: { label: "En attente de paiement", cls: "bg-amber-100 text-amber-700" },
  payee: { label: "Payée", cls: "bg-green-100 text-green-700" },
  annulee: { label: "Annulée", cls: "bg-red-100 text-red-700" },
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [visaFiles, setVisaFiles] = useState([]);

  const load = () => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/visa/files").then(({ data }) => setVisaFiles(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h1 className="font-head text-4xl font-extrabold text-primary mb-1">Bonjour, {user?.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground mb-8">Gérez vos réservations et vos dossiers visa.</p>

      <Tabs defaultValue="bookings">
        <TabsList className="rounded-full mb-6">
          <TabsTrigger value="bookings" className="rounded-full" data-testid="tab-bookings"><Luggage className="h-4 w-4 mr-2" /> Mes réservations</TabsTrigger>
          <TabsTrigger value="visa" className="rounded-full" data-testid="tab-visa-files"><FileText className="h-4 w-4 mr-2" /> Mes dossiers visa</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          {bookings.length === 0 ? (
            <EmptyState text="Vous n'avez pas encore de réservation." cta={<Link to="/offers"><Button className="rounded-full bg-accent text-accent-foreground">Voir les offres</Button></Link>} />
          ) : (
            <div className="space-y-4" data-testid="bookings-list">
              {bookings.map((b) => {
                const badge = BOOKING_BADGE[b.status] || BOOKING_BADGE.en_attente_paiement;
                return (
                  <div key={b.id} className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`booking-${b.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${badge.cls}`} data-testid={`booking-status-${b.id}`}>{badge.label}</span>
                      </div>
                      <h3 className="font-head font-bold text-lg text-primary">{b.offer_title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {b.destination}</span>
                        {b.departure_country && <span className="flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> Depuis {b.departure_country}</span>}
                        <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {new Date(b.depart_date || b.date).toLocaleDateString("fr-FR")}{b.return_date ? ` → ${new Date(b.return_date).toLocaleDateString("fr-FR")}` : ""}</span>
                        <span>{b.num_people} pers.</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-head text-xl font-extrabold text-accent">{fcfa(b.total_amount)}</div>
                      {b.status === "en_attente_paiement" && (
                        <Link to={`/client/payment/${b.id}`}>
                          <Button size="sm" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 mt-2" data-testid={`pay-btn-${b.id}`}>
                            Payer <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="visa">
          {visaFiles.length === 0 ? (
            <EmptyState text="Aucun dossier visa ouvert." cta={<Link to="/visa"><Button className="rounded-full bg-accent text-accent-foreground">Découvrir les parcours</Button></Link>} />
          ) : (
            <div className="space-y-4" data-testid="visa-files-list">
              {visaFiles.map((v) => (
                <Link to={`/client/visa/${v.id}`} key={v.id} className="block bg-white rounded-2xl soft-shadow border border-gray-100 p-5 card-hover" data-testid={`visa-file-${v.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-head font-bold text-lg text-primary">{v.pathway_name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">Étape actuelle : <span className="font-semibold text-primary">{v.steps[v.current_step_index]}</span></p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-accent" />
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: `${((v.current_step_index + 1) / v.steps.length) * 100}%` }} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyState({ text, cta }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
      <p className="text-muted-foreground mb-4">{text}</p>
      {cta}
    </div>
  );
}
